import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Phone, Building2, ArrowRight } from "lucide-react";
import { MarketingShell, Container, Reveal, Eyebrow, TrialButton, useSeo, FONT } from "./Marketing";
import { submitContact } from "../../lib/api";
import { track } from "../../lib/analytics";

export default function Contact() {
  useSeo("Book a Demo — AI Turf Designer", "Talk to us about adding an AI turf designer to your website. Book a demo or ask a question — we'll be in touch shortly.");
  useEffect(() => { track("homepage_view", { page: "contact" }); }, []);
  const [f, setF] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const valid = f.name.trim() && /.+@.+\..+/.test(f.email);

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await submitContact({ ...f, source_page: "contact" });
      track("contact_submitted", { source_page: "contact" });
      setDone(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally { setBusy(false); }
  };

  const input = "w-full bg-[#1A1A1A] border border-white/10 rounded-md px-4 py-3 text-white text-sm outline-none focus:border-[#00D26A] focus:ring-1 focus:ring-[#00D26A] transition-colors";

  return (
    <MarketingShell>
      <section className="py-20 md:py-28">
        <Container className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <Reveal><Eyebrow>Book a demo / talk to us</Eyebrow></Reveal>
            <Reveal delay={0.05}><h1 className="mt-4 text-4xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none" style={{ fontFamily: FONT.display }}>See how it fits your business</h1></Reveal>
            <Reveal delay={0.1}><p className="mt-5 text-zinc-400 text-lg leading-relaxed">Want a walkthrough before you start? Tell us a little about your company and we'll show you exactly how the designer captures leads for turf and gym-flooring installers.</p></Reveal>
            <Reveal delay={0.16}>
              <div className="mt-8 space-y-4">
                {[[Building2, "Perfect for", "Turf installers, gym flooring & sports facility companies"], [Mail, "Prefer to just start?", "Skip the demo — spin up a free trial in minutes"], [Phone, "Response time", "We typically reply within one business day"]].map(([I, t, d]) => (
                  <div key={t} className="flex gap-3"><I className="text-[#00D26A] shrink-0 mt-0.5" size={20} /><div><div className="text-white font-semibold text-sm">{t}</div><div className="text-zinc-400 text-sm">{d}</div></div></div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.2}><div className="mt-8"><TrialButton source="contact" variant="secondary">Or start a free trial <ArrowRight size={16} className="ml-2" /></TrialButton></div></Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="bg-[#161616] border border-white/10 rounded-2xl p-7 md:p-9" data-testid="contact-form">
              {done ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="w-14 h-14 rounded-full bg-[#00D26A]/15 border border-[#00D26A]/40 flex items-center justify-center mx-auto"><ArrowRight className="text-[#00D26A]" /></div>
                  <h3 className="mt-5 text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: FONT.display }}>Message sent</h3>
                  <p className="mt-3 text-zinc-400">Thanks {f.name.split(" ")[0]} — we've got your enquiry and will be in touch shortly.</p>
                  <TrialButton source="contact-success" className="mt-6">Start a free trial now</TrialButton>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-black uppercase tracking-tight text-white" style={{ fontFamily: FONT.display }}>Request a demo</h2>
                  <p className="text-zinc-500 text-sm mt-1 mb-6">We'll get back to you personally.</p>
                  <div className="space-y-4">
                    <div><label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Your name*</label><input className={input} value={f.name} onChange={set("name")} data-testid="contact-name" /></div>
                    <div><label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Work email*</label><input type="email" className={input} value={f.email} onChange={set("email")} data-testid="contact-email" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Company</label><input className={input} value={f.company} onChange={set("company")} data-testid="contact-company" /></div>
                      <div><label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Phone</label><input className={input} value={f.phone} onChange={set("phone")} data-testid="contact-phone" /></div>
                    </div>
                    <div><label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1.5">How can we help?</label><textarea rows={4} className={input} value={f.message} onChange={set("message")} data-testid="contact-message" /></div>
                  </div>
                  <button onClick={submit} disabled={!valid || busy} data-testid="contact-submit" className="w-full mt-6 bg-[#00D26A] text-black font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md hover:bg-[#00E574] transition-colors disabled:opacity-40">{busy ? "Sending…" : "Send enquiry"}</button>
                </>
              )}
            </div>
          </Reveal>
        </Container>
      </section>
    </MarketingShell>
  );
}
